// chartA11y.js — pure, i18n-agnostic accessibility helpers for chart
// screen-reader support (Tier-1 a11y / Gate B).
//
// This module does NOT import the i18n setup: every localizable string is
// produced by calling the caller-supplied `t(key, params)` translator
// (react-i18next signature — see resolveErrorMessage in ./errorMessage.js for
// the same "translator injected" pattern used elsewhere in this codebase).
// That keeps this module fully unit-testable with a stub `t` and decoupled
// from the actual translation files.
//
// No import of InteractiveGraph.js and no logic extracted from it — every
// function below is new, standalone logic.

const AXIS_TYPE_PATTERN = /axis/i;

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function findElementById(elements, id) {
  if (!Array.isArray(elements)) return null;
  return elements.find((el) => el && el.id === id) || null;
}

// Axis label precedence: graphData.elements[axisId].label (non-empty) ->
// stageData.{x_label,y_label} (non-empty) -> translated fallback string.
// Mirrors the existing InteractiveGraph fallback chain (see e.g.
// `axisXData?.label || stageData?.x_label || t(...)` around IG:6343-6344),
// but implemented fresh here as pure, standalone logic.
function resolveAxisLabel(elements, axisId, stageFallback, t, fallbackKey) {
  const axisElement = findElementById(elements, axisId);
  if (axisElement && isNonEmptyString(axisElement.label)) {
    return axisElement.label;
  }
  if (isNonEmptyString(stageFallback)) {
    return stageFallback;
  }
  return t(fallbackKey);
}

// Number of non-axis (data-bearing) elements — bars, lines, slices, etc.
function countDataElements(elements) {
  if (!Array.isArray(elements)) return 0;
  return elements.filter((el) => el && el.id && !AXIS_TYPE_PATTERN.test(el.type || '')).length;
}

// Stage title precedence: stage_summary -> description -> title -> fallback.
function resolveStageTitle(stageData, t) {
  if (stageData) {
    if (isNonEmptyString(stageData.stage_summary)) return stageData.stage_summary;
    if (isNonEmptyString(stageData.description)) return stageData.description;
    if (isNonEmptyString(stageData.title)) return stageData.title;
  }
  return t('a11y.chartSummary.stageTitleFallback');
}

/**
 * Compose a natural-language, screen-reader-friendly summary of the current
 * chart + stage. Pure function: all localizable text goes through the
 * injected `t`, all sentence assembly happens in the single
 * `a11y.chartSummary.full` template so translators control word order.
 *
 * @param {object} params
 * @param {object} [params.graphData]      backend graph payload ({graph_type, elements, stages, total_stages})
 * @param {object} [params.stageData]      the currently active stage object
 * @param {number} [params.currentStageNum] 0-indexed current stage
 * @param {number} [params.totalStages]    total stage count (falls back to graphData.stages.length)
 * @param {function} params.t              translator: (key, params?) => string
 * @returns {string} composed summary
 */
export function buildChartSummary({ graphData, stageData, currentStageNum, totalStages, t } = {}) {
  const translate = typeof t === 'function' ? t : (key) => key;
  // Legacy sessions deliver the payload nested one level down
  // (graphData.graph_data.{graph_type,elements,...}); the renderers all
  // normalize with the same `graphData.graph_data || graphData` idiom (see
  // e.g. renderScatterPlot's actualGraphData). Without this, a nested payload
  // renders fine but the accessible summary would report a generic chart
  // with zero series and fallback axes.
  const normalizedGraph = (graphData && graphData.graph_data) || graphData;
  const elements = normalizedGraph && Array.isArray(normalizedGraph.elements) ? normalizedGraph.elements : [];

  const graphTypeKey = isNonEmptyString(normalizedGraph?.graph_type) ? normalizedGraph.graph_type : 'generic';
  const graphTypeLabel = translate(`graphType.${graphTypeKey}`);

  const xAxisLabel = resolveAxisLabel(
    elements, 'axis_x', stageData?.x_label, translate, 'a11y.chartSummary.xAxisFallback'
  );
  const yAxisLabel = resolveAxisLabel(
    elements, 'axis_y', stageData?.y_label, translate, 'a11y.chartSummary.yAxisFallback'
  );

  const elementCount = countDataElements(elements);

  const resolvedTotalStages = Number.isFinite(totalStages) && totalStages > 0
    ? totalStages
    : (Array.isArray(normalizedGraph?.stages) ? normalizedGraph.stages.length : 0);
  const stageNumber = Number.isFinite(currentStageNum) ? currentStageNum + 1 : 1;

  const stageTitle = resolveStageTitle(stageData, translate);

  return translate('a11y.chartSummary.full', {
    graphType: graphTypeLabel,
    xAxisLabel,
    yAxisLabel,
    elementCount,
    stageNumber,
    totalStages: resolvedTotalStages,
    stageTitle
  });
}

/**
 * Decide whether a stage-change aria-live announcement should fire, given a
 * simple identity key per stage (e.g. `${graphId}:${stageNum}`) and a
 * minimum re-announcement interval. Pure — no timers, no DOM.
 *
 * Rules (evaluated in order):
 *   1. Same stage key as last time -> never re-announce -> false.
 *   2. No prior announcement timestamp -> not throttled -> true.
 *   3. Elapsed (now - lastAnnounceTs) < minIntervalMs -> throttled -> false.
 *   4. Otherwise -> true.
 *
 * @param {*} prevStageKey
 * @param {*} nextStageKey
 * @param {number|null|undefined} lastAnnounceTs epoch ms of the last announcement, or null/undefined if none yet
 * @param {number} now epoch ms "now"
 * @param {number} minIntervalMs minimum ms between announcements
 * @returns {boolean}
 */
export function shouldAnnounceStage(prevStageKey, nextStageKey, lastAnnounceTs, now, minIntervalMs) {
  if (nextStageKey === prevStageKey) return false;

  const hasPriorAnnouncement = Number.isFinite(lastAnnounceTs);
  if (!hasPriorAnnouncement) return true;

  const current = Number.isFinite(now) ? now : 0;
  const interval = Number.isFinite(minIntervalMs) ? minIntervalMs : 0;
  const elapsed = current - lastAnnounceTs;
  if (elapsed < interval) return false;

  return true;
}

const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Spacebar']);

/**
 * True for the keyboard keys that should activate a chart element treated as
 * `role="button"` (Enter, Space, and the legacy IE/old-browser 'Spacebar'
 * value some UAs still send for KeyboardEvent.key).
 *
 * @param {string} key KeyboardEvent.key value
 * @returns {boolean}
 */
export function isActivationKey(key) {
  return ACTIVATION_KEYS.has(key);
}
